import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  User, 
  Briefcase, 
  TrendingUp, 
  Trophy, 
  Star, 
  Clock, 
  AlertCircle,
  ArrowRight,
  MapPin,
  Lock,
  Scale,
  BarChart3
} from "lucide-react";

const NIVEAU_CONFIG = {
  premium: { color: "bg-accent", text: "Premium", icon: Trophy },
  qualifie: { color: "bg-primary", text: "Qualifié", icon: Star },
  en_construction: { color: "bg-orange-500", text: "En construction", icon: Clock },
  curieux: { color: "bg-gray-500", text: "Curieux", icon: AlertCircle },
};

const UserDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCA = (value) => {
    if (!value) return "-";
    if (typeof value === "string" && value.includes("€")) {
        return value;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return formatCurrency(num);
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("access_token");
      const userType = localStorage.getItem("user_type");

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const endpoint = userType === "cedant" ? `${API}/cedants/me/profile` : `${API}/repreneurs/me/profile`;
        const profileRes = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(profileRes.data);

        const oppsRes = await axios.get(`${API}/opportunites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOpportunities(oppsRes.data);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user_type");
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
        <p className="text-xl font-medium text-primary">Chargement de votre espace...</p>
      </div>
    );
  }

  if (!profile) return null;

  const userType = localStorage.getItem("user_type");
  const isCedant = userType === "cedant";

  return (
    <div className="max-w-7xl mx-auto px-8 py-12">
      <div className="mb-12">
        <h1 className="text-4xl font-black text-primary mb-2">
          Bonjour, {profile.prenom} !
        </h1>
        <p className="text-lg text-foreground/60 font-medium">
          Ravi de vous revoir sur Sequenti. Voici un aperçu de votre activité.
        </p>
      </div>

      {!profile.validation && (
        <Card className="mb-12 border-none bg-orange-50 border-l-4 border-orange-500 rounded-lg overflow-hidden shadow-sm">
          <CardContent className="flex items-center gap-6 p-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-orange-900 mb-2">
                Votre compte est en cours de validation
              </h3>
              <p className="text-orange-800/80 font-medium leading-relaxed">
                Merci de votre patience. Un administrateur de Sequenti examine actuellement votre profil. 
                Certaines fonctionnalités seront disponibles une fois votre compte validé.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-8">
        {/* Profile Recap Column */}
        <div className="space-y-8">
          <Card className="border-none shadow-sm overflow-hidden">
            <div className="h-2 bg-accent w-full" />
            <CardContent className="pt-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-primary capitalize">
                    {profile.prenom} {profile.nom}
                  </h2>
                  <p className="text-foreground/40 font-medium text-sm">
                    {isCedant ? "Cédant" : "Repreneur"}
                  </p>
                </div>
              </div>

              {/* Specific recap info */}
              {!isCedant && (
                <div className="mt-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-foreground/40 uppercase tracking-wider">Score Qualification</span>
                    <span className="text-sm font-bold text-accent">{profile.score}/100</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-accent" style={{ width: `${profile.score}%` }} />
                  </div>
                  {profile.niveau && (
                    <Badge className={`${NIVEAU_CONFIG[profile.niveau]?.color || "bg-gray-500"} text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 w-fit`}>
                      {React.createElement(NIVEAU_CONFIG[profile.niveau]?.icon || AlertCircle, { className: "w-3 h-3" })}
                      Profil {NIVEAU_CONFIG[profile.niveau]?.text || profile.niveau}
                    </Badge>
                  )}
                </div>
              )}

              {isCedant && (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3 text-foreground/70">
                    <Briefcase className="w-5 h-5 text-accent" />
                    <span className="font-bold text-primary">{profile.entreprise || "Dossier Confidentiel"}</span>
                  </div>
                  {profile.valuation_estimee && (
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                      <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-widest mb-1">Valorisation moyenne</p>
                      <p className="text-xl font-black text-accent">
                        {formatCurrency(profile.valuation_estimee.moyenne)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button 
                variant="outline" 
                className="w-full border-accent text-accent hover:bg-accent/5 rounded-full mt-8"
                onClick={() => navigate("/profile")}
              >
                Voir mon profil complet
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Opportunities/Activity Column */}
        <div className="md:col-span-2 space-y-12">
          {!isCedant ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-primary">Dernières opportunités</h3>
                {profile.validation && (
                  <Button 
                    variant="ghost" 
                    className="text-accent font-bold flex items-center gap-2"
                    onClick={() => navigate("/opportunites")}
                  >
                    Tout voir <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {!profile.validation ? (
                <Card className="p-12 text-center border-none bg-muted rounded-lg relative overflow-hidden group border border-border/40">
                  <div className="absolute inset-0 bg-white/20 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-8">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
                      <Lock className="w-8 h-8 text-accent" />
                    </div>
                    <h4 className="text-xl font-bold text-primary mb-3">Opportunités réservées</h4>
                    <p className="text-foreground/60 max-w-md mx-auto font-medium leading-relaxed">
                      L'accès aux opportunités de reprise est réservé aux repreneurs dont le profil a été validé par Sequenti. 
                      Vous serez notifié dès que votre compte sera prêt.
                    </p>
                  </div>
                  
                  {/* Blurred background content for visual hint */}
                  <div className="grid gap-4 opacity-10 pointer-events-none grayscale">
                    {[1, 2].map(i => (
                      <div key={i} className="p-6 bg-white rounded-lg flex justify-between items-center">
                        <div className="space-y-2">
                          <div className="h-4 w-20 bg-gray-200 rounded" />
                          <div className="h-6 w-48 bg-gray-300 rounded" />
                        </div>
                        <div className="h-10 w-24 bg-gray-200 rounded" />
                      </div>
                    ))}
                  </div>
                </Card>
              ) : opportunities.length === 0 ? (
                <Card className="p-12 text-center border-none bg-muted rounded-lg">
                  <p className="text-lg font-medium text-foreground/40">Aucune opportunité disponible pour le moment.</p>
                </Card>
              ) : (
                <div className="grid gap-6">
                  {opportunities.slice(0, 3).map((opp) => (
                    <Card 
                      key={opp.id} 
                      className="p-6 bg-white border-none shadow-sm hover:shadow-md transition-all duration-300 rounded-lg cursor-pointer flex flex-col relative overflow-hidden group"
                      onClick={() => navigate("/opportunites")}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <Badge className="bg-primary/10 text-primary border-none px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                            {opp.secteur}
                          </Badge>
                          <h4 className="text-xl font-bold text-primary group-hover:text-accent transition-colors">{opp.titre}</h4>
                          <div className="flex items-center gap-2 text-sm text-foreground/60 font-medium">
                            <MapPin className="w-4 h-4 text-accent" />
                            {opp.localisation}
                          </div>
                        </div>
                        <div className="flex items-center gap-8 border-t md:border-t-0 md:border-l border-border/50 pt-4 md:pt-0 md:pl-8">
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase font-bold text-foreground/40 tracking-widest">CA</span>
                            <span className="font-bold text-primary">{formatCA(opp.chiffre_affaires)}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase font-bold text-foreground/40 tracking-widest">Effectif</span>
                            <span className="font-bold text-primary">{opp.effectif} pers.</span>
                          </div>
                          <ArrowRight className="w-6 h-6 text-accent opacity-0 group-hover:opacity-100 transition-all translate-x-[-10px] group-hover:translate-x-0" />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Estimation Overview Section */}
              {profile.valuation_estimee && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-primary">Synthèse de l'estimation</h3>
                    <Button 
                      variant="ghost" 
                      className="text-accent font-bold flex items-center gap-2"
                      onClick={() => navigate("/cedant/resultat")}
                    >
                      Rapport détaillé <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Card className="p-8 border-none bg-white shadow-sm rounded-lg overflow-hidden relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-accent" />
                    <div className="grid md:grid-cols-3 gap-8">
                      <div className="space-y-1">
                        <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-widest">Fourchette basse</p>
                        <p className="text-xl font-bold text-primary">{formatCurrency(profile.valuation_estimee.fourchette_basse)}</p>
                      </div>
                      <div className="space-y-1 text-center bg-muted/30 py-4 rounded-lg border border-border/40">
                        <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-widest">Valeur moyenne</p>
                        <p className="text-3xl font-black text-accent">{formatCurrency(profile.valuation_estimee.moyenne)}</p>
                      </div>
                      <div className="space-y-1 text-right">
                        <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-widest">Fourchette haute</p>
                        <p className="text-xl font-bold text-primary">{formatCurrency(profile.valuation_estimee.fourchette_haute)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-border/40">
                      <div className="flex flex-col items-center text-center px-2">
                        <div className="w-8 h-8 bg-primary/5 rounded-full flex items-center justify-center mb-2">
                          <Scale className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-[9px] font-bold text-foreground/40 uppercase tracking-tighter mb-1">Patrimoniale</p>
                        <p className="text-sm font-bold text-primary">{formatCurrency(profile.valuation_estimee.patrimoniale)}</p>
                      </div>
                      <div className="flex flex-col items-center text-center px-2 border-x border-border/40">
                        <div className="w-8 h-8 bg-primary/5 rounded-full flex items-center justify-center mb-2">
                          <TrendingUp className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-[9px] font-bold text-foreground/40 uppercase tracking-tighter mb-1">Rendement</p>
                        <p className="text-sm font-bold text-primary">{formatCurrency(profile.valuation_estimee.rendement)}</p>
                      </div>
                      <div className="flex flex-col items-center text-center px-2">
                        <div className="w-8 h-8 bg-primary/5 rounded-full flex items-center justify-center mb-2">
                          <BarChart3 className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-[9px] font-bold text-foreground/40 uppercase tracking-tighter mb-1">Comparative</p>
                        <p className="text-sm font-bold text-primary">{formatCurrency(profile.valuation_estimee.comparative)}</p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
